/**
 * Knowledge Node Types
 *
 * KnowledgeNode is a canvas node that provides access to a board's
 * Knowledge Space. It displays documents, allows uploads, and enables
 * users to ask questions that get answered using RAG retrieval.
 *
 * Key features:
 * - Displays documents in the board's knowledge space
 * - Upload interface for adding new documents
 * - Question input with Ask button
 * - Streams answers to connected BlockNode (or shows inline if not connected)
 * - Citations displayed with source references
 */

import type { KnowledgeDocument, KnowledgeCitation } from './knowledge';

// Position type for Flow nodes
type FlowPosition = { x: number; y: number };

// ============================================================================
// STATUS TYPES
// ============================================================================

/**
 * Status of the KnowledgeNode:
 * - idle: Ready for input
 * - loading: Fetching space/documents
 * - asking: Processing a question
 * - error: An error occurred
 */
export type KnowledgeNodeStatus = 'idle' | 'loading' | 'asking' | 'error';

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * Data structure for KnowledgeNode instances.
 *
 * This is stored in the React Flow node's data property and synced
 * via Yjs for real-time collaboration.
 */
export interface KnowledgeNodeData {
  // ========== Identity ==========

  /** Node title (optional, displayed in header) */
  title?: string;

  // ========== Knowledge Space ==========

  /** Board's knowledge space ID (auto-fetched on mount) */
  spaceId?: string;

  /** Cached list of documents in the space */
  documents?: KnowledgeDocument[];

  // ========== State ==========

  /** Current node status */
  status: KnowledgeNodeStatus;

  /** Error message if status is 'error' */
  error?: string;

  /** Last question asked (for display) */
  lastQuestion?: string;

  /** Last answer received (shown inline when not connected) */
  lastAnswer?: string;

  /** Citations from last answer */
  lastCitations?: KnowledgeCitation[];

  // ========== Runtime Props (injected by BoardCanvas) ==========

  /** Board ID for API calls */
  boardId?: string;

  /** Yjs map of nodes (for creating output BlockNodes) */
  nodesMap?: any; // Y.Map<Node>

  /** Yjs map of edges (for creating connections) */
  edgesMap?: any; // Y.Map<Edge>

  /** Yjs document for collaboration */
  ydoc?: any; // Y.Doc

  /** Wrapper for undo/redo support */
  makeChange?: (fn: () => void) => void;

  /** Callback when title changes */
  onTitleChange?: (title: string) => void;

  /** Callback when status changes */
  onStatusChange?: (status: KnowledgeNodeStatus) => void;

  // Index signature required for React Flow compatibility
  [key: string]: unknown;
}

// ============================================================================
// REACT FLOW NODE TYPE
// ============================================================================

/**
 * React Flow node type for KnowledgeNode.
 */
export type KnowledgeNodeType = {
  id: string;
  type: 'knowledge';
  data: KnowledgeNodeData;
  position: FlowPosition;
  width?: number;
  height?: number;
  [key: string]: unknown;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create default KnowledgeNodeData with sensible defaults.
 */
export function createDefaultKnowledgeNodeData(): KnowledgeNodeData {
  return {
    title: '',
    status: 'idle',
    documents: [],
  };
}
